import { Form, Formik } from 'formik';
import React, { useContext, useEffect, useRef, useState } from 'react';
import * as Yup from 'yup';

import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';

import Confirm from '../../../components/confirm';
import Wait from '../../../components/wait';
import { AppContext } from '../../../contexts/app-context';
import GlobalStyles from '../../../global-styles';
import API from '../../../utils/api';
import {
  apiReponseError, apiRequestError, canReadDeviceLocation, canWriteDeviceLocation, getErrorObject,
  isFormDataValid
} from '../../../utils/helper';
import { DeleteIcon } from '../../../utils/icons';
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, TextField, Typography
} from '../../../utils/material-ui-core';

interface devicelocationCRUDProps {
  open: boolean;
  deviceLocationKey: string;
  name: string;
  onCallback: (confirmOutput: ConfirmOutput) => void;
}

// Default form value
const formDefaultValue = {
  deviceLocationKey: "",
  name: "",
};

const formDefaultTouchValue = {
  name: false,
};

// Form validation schema
const formValidationSchema = Yup.object().shape({
  name: Yup.string().trim().required("Name is required."),
});

const newDeviceLocationDefaultValue = {
  deviceLocationKey: "",
  name: "",
};

const DeviceLocationCRUD = ({ open, name, deviceLocationKey, onCallback }: devicelocationCRUDProps) => {
  const pageStyles = LocalPageStyles();
  const globalStyles = GlobalStyles();

  const formReference = useRef(null) as any;

  const { refreshData, referenceLookups, showAlert, userFunctionAccess } = useContext(AppContext);
  const [loading, setLoading] = useState<boolean>(true);
  const [readonly, setReadonly] = useState<boolean>(true);
  const [isNew, setIsNew] = useState<boolean>(true);
  const [saveButtonVisible, setSaveButtonVisible] = useState<boolean>(false);
  const [saveInProgress, setSaveInProgress] = useState<boolean>(false);
  const [initialFormValues, setInitialFormValues] = useState<DeviceLocation>(formDefaultValue);
  const [deleteConfirmDialogOpenForSKU, setDeleteConfirmDialogOpenForDeviceLocation] = useState<boolean>(false);

  const showDeleteConfirmationDialog = () => {
    setDeleteConfirmDialogOpenForDeviceLocation(true);
  };

  const deleteDeviceLocation = async (confirm: boolean) => {
    setDeleteConfirmDialogOpenForDeviceLocation(false);

    if (confirm) {
      setLoading(true);
      try {
        await API.delete("/api/onboarding/devicelocations/" + deviceLocationKey)
          .then(async (response) => {
            showAlert({
              message: "Successfully removed.",
              severity: "success",
            });
            await refreshData("devicelocations");
            onCallback({ status: "success", data: "" });
          })
          .catch((error) => {
            showAlert({
              message: apiReponseError(error, "Failed to delete Device Location."),
              severity: "error",
            });
            setLoading(false);
          });
      } catch (e) {
        showAlert({
          message: apiRequestError(e, "Something went wrong in our system."),
          severity: "error",
        });
        setLoading(false);
      }
    }
  };

  const saveDeviceLocation = async () => {
    if (!canWriteDeviceLocation(userFunctionAccess)) {
      showAlert({ message: "Access Denied.", severity: "error" });
      cancel();
      return;
    }

    const payload = {
      deviceLocation: {
        deviceLocationKey: formReference.current.values.deviceLocationKey === "" ? "00000000-0000-0000-0000-000000000000" : formReference.current.values.deviceLocationKey,
        name: formReference.current.values.name,
      },
    };

    try {
      setSaveInProgress(true);
      await API.post("/api/onboarding/devicelocations/" + formReference.current.values.deviceLocationKey, payload)
        .then(async (response) => {
          await refreshData("devicelocations");
          onCallback({ status: "success", data: "" });
        })
        .catch((error) => {
          setSaveInProgress(false);
          const errorList: ApiResponseErrorObject[] = getErrorObject(error, "deviceLocation.");
          let isErrorAssignedAtFieldLevel = true;
          try {
            errorList.forEach(function (error) {
              formReference.current.setFieldError(error.title, error.message.join(" "));
            });
          } catch (e) {
            isErrorAssignedAtFieldLevel = false;
          }
          isErrorAssignedAtFieldLevel = false;
          if (errorList.length <= 0 || !isErrorAssignedAtFieldLevel) {
            showAlert({
              message: apiReponseError(error, "Failed to save device location data."),
              severity: "error",
            });
          }
        });
    } catch (e) {
      setSaveInProgress(false);
      showAlert({
        message: apiRequestError(e, "Something went wrong in our system."),
        severity: "error",
      });
    }
  };

  const cancelDeviceLocation = () => {
    formReference.current.resetForm({});
    onCallback({ status: "cancel", data: "" });
  };

  // Other
  const cancel = () => {
    onCallback({ status: "cancel", data: "" });
  };

  useEffect(() => {
    const initialPageAsync = async () => {
      if (open) {
        setLoading(true);
        setReadonly(true);
        setSaveButtonVisible(false);

        if (isFormDataValid(formReference)) {
          formReference.current.resetForm({});
        }

        if (!canReadDeviceLocation(userFunctionAccess)) {
          showAlert({ message: "Access Denied.", severity: "error" });
          cancel();
          return;
        }

        if (deviceLocationKey === "") {
          if (!canWriteDeviceLocation(userFunctionAccess)) {
            showAlert({ message: "Access Denied.", severity: "error" });
            cancel();
            return;
          }

          setInitialFormValues({
            ...newDeviceLocationDefaultValue,
            deviceLocationKey: referenceLookups.newRecordDefaultId,
          } as DeviceLocation);
          setIsNew(true);
          setReadonly(false);
          setLoading(false);
        } else {
          setInitialFormValues({
            ...newDeviceLocationDefaultValue,
            deviceLocationKey: deviceLocationKey,
            name: name,
          } as DeviceLocation);
          setIsNew(false);
          setReadonly(!canWriteDeviceLocation(userFunctionAccess));
          setLoading(false);
        }
      }
    };

    initialPageAsync();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, name, deviceLocationKey, open]);

  return (
    <>
      {formReference.current !== null ? (
        <>
          <Confirm title="Confirmation" content={`<p>Are you sure you want to delete this Device Location?</p><p style="text-align:center"><b>${formReference.current.values.name}</b></p><p style="text-align:center"></p>`} yesText="DELETE" noText="CANCEL" open={deleteConfirmDialogOpenForSKU} callbackOnClose={() => setDeleteConfirmDialogOpenForDeviceLocation} callbackOnConfirm={deleteDeviceLocation} />
        </>
      ) : null}

      <Dialog className={globalStyles.dialogContainer} fullWidth={true} open={open}>
        <DialogTitle className={globalStyles.dialogTitleContainer}>
          <Typography variant="subtitle1" component="div">
            {readonly ? "View Device Location" : isNew ? "New Device Location" : "Edit Device Location"}
          </Typography>
        </DialogTitle>
        {loading ? (
          <>
            <DialogContent dividers>
              <Wait />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => cancel()} color="secondary" autoFocus>
                <u>Cancel</u>
              </Button>
            </DialogActions>
          </>
        ) : (
          <>
            <DialogContent dividers className={globalStyles.formikContainer}>
              <Box className={pageStyles.contentText}>
                <Formik
                  innerRef={formReference}
                  initialValues={initialFormValues}
                  validationSchema={formValidationSchema}
                  validateOnMount={true}
                  initialTouched={formDefaultTouchValue}
                  validateOnBlur={true}
                  validateOnChange={true}
                  onSubmit={(values: DeviceLocation) => {
                    saveDeviceLocation();
                  }}
                >
                  {({ values, errors, handleChange, handleSubmit, touched, handleBlur, isSubmitting, setFieldValue, setFieldError, resetForm }) => (
                    <Form
                      onChange={() => {
                        setSaveButtonVisible(true);
                      }}
                    >
                      <Grid container spacing={3}>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth={true}
                            required
                            disabled={readonly}
                            id="name"
                            name="name"
                            label="Name"
                            value={values.name}
                            onChange={(e) => {
                              handleChange(e);
                            }}
                            onBlur={handleBlur}
                            error={touched.name && Boolean(errors.name)}
                            helperText={errors.name && touched.name ? errors.name : null}
                          />
                        </Grid>
                      </Grid>
                    </Form>
                  )}
                </Formik>
              </Box>
            </DialogContent>
            <DialogActions>
              {!readonly && !isNew ? (
                <Button onClick={() => showDeleteConfirmationDialog()} className={globalStyles.errorText}>
                  <DeleteIcon />
                </Button>
              ) : null}

              {!readonly ? (
                <Button disabled={!saveButtonVisible || saveInProgress} onClick={() => formReference.current.handleSubmit()} color="secondary" variant="contained" className={globalStyles.buttonDefault}>
                  {isNew ? "Save" : "Update"}
                </Button>
              ) : null}

              <Button onClick={() => cancelDeviceLocation()} autoFocus className={globalStyles.buttonDefault}>
                <u>Cancel</u>
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </>
  );
};

const LocalPageStyles = makeStyles((theme: Theme) =>
  createStyles({
    contentText: {
      whiteSpace: "pre-wrap",
      minHeight: "150px",
    },
  })
);

export default DeviceLocationCRUD;
